import { transition } from '@helpers/animate';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { baseGrid } from '@helpers/grid';
import { Layers } from '@helpers/layers';
import { Device, from, until } from '@helpers/media';
import { h2, h3, paragraphLarge, paragraphSmall } from '@helpers/typography';
import Button from '@stories/Components/Buttons/Button/Button';
import PaginationStyles from '@stories/Components/Misc/Pagination/Pagination.styles';
import SectionWrapper from '@stories/Components/Misc/SectionWrapper/SectionWrapper';
import SectionWrapperStyles from '@stories/Components/Misc/SectionWrapper/SectionWrapper.styles';
import SearchResultCardStyles from '@stories/Components/SearchResultCard/SearchResultCard.styles';
import styled, { css } from 'styled-components';
const filterToggleStyles = css `
  --iconSize: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  column-gap: ${fluid(6, 12)};
  height: 70px;
  border-radius: 12px;
  color: ${brand.primary.blue};
  padding: 20px ${fluid(12, 18)};
  border: 1px solid ${brand.grey.medium};
  background-color: ${brand.grey.light};
  cursor: pointer;

  svg {
    width: var(--iconSize);
    height: var(--iconSize);
    flex-shrink: 0;
    opacity: 0.5;
  }
`;
const leftColumns = css `
  grid-column: 1 / -1;
`;
const Filters = styled.div `
  width: 100%;
  max-width: 310px;
  flex-shrink: 0;

  @media ${from(Device.Tablet)} {
    position: relative;
    order: 1;
    background-color: ${brand.grey.light};

    &::before {
      content: '';
      position: absolute;
      top: 0;
      right: 1px;
      width: calc(var(--sitePadding) + 1px);
      height: 100%;
      background-color: ${brand.grey.light};
      transform: translateX(100%);
    }
  }
`;
const Container = styled(SectionWrapper) `
  --sectionSpacingTop: 48px;
  position: relative;

  @media ${from(Device.Tablet)} {
    &:has(${Filters}) {
      &::before {
        content: '';
        position: absolute;
        top: var(--sectionSpacingTop);
        right: 0;
        width: 50vw;
        height: calc(100% - var(--sectionSpacingTop) - var(--sectionSpacingBottom));
        background-color: ${brand.grey.light};
      }
    }
  }

  ${SectionWrapperStyles.Inner} {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: ${fluid(32, 40)};
    background-color: ${brand.global.white};

    @media ${from(Device.Tablet)} {
      flex-direction: row;
    }
  }

  ${PaginationStyles.Container} {
    margin: 0 0 ${fluid(48, 64)};
  }

  ${PaginationStyles.Container},
  ${SearchResultCardStyles.Container} {
    ${leftColumns};
  }

  ${SearchResultCardStyles.Container} {
    margin-bottom: ${fluid(16, 40)};

    &:last-of-type {
      margin-bottom: 0;
    }
  }

  @media ${from(Device.TabletLarge)} {
    --sectionSpacingTop: 32px;
  }
`;
const ResultsBlock = styled.div `
  ${baseGrid};

  width: 100%;
  row-gap: 0;

  ${SearchResultCardStyles.Container} {
    &:last-of-type {
      margin-bottom: ${fluid(32, 40)};
    }
  }
`;
const ResultsHeading = styled.div `
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  grid-column: span 12;
  gap: 12px;
  margin-bottom: 40px;

  @media ${from(Device.Tablet)} {
    flex-direction: row;
    align-items: center;
  }
`;
const ResultsTitle = styled.h2 `
  ${h2};

  display: flex;
  align-items: center;
  column-gap: 12px;
  margin-bottom: 0;

  @media ${until(Device.Tablet)} {
    justify-content: space-between;
  }
`;
const ResultsCount = styled.span `
  min-height: 36px;
  padding: 8px;
  font-size: 14px;
  line-height: 1.5;
  border-radius: 4px;
  border: 1px solid ${brand.primary.blue};
  ${fonts.dmSans.regular};
`;
const FilteringWrapper = styled.div `
  display: flex;
  align-items: center;
  gap: 16px;

  @media ${until(Device.Tablet)} {
    > * {
      width: 50%;
    }
  }
`;
const ResultsSort = styled.div `
  position: relative;
`;
const SortLabel = styled.label `
  ${filterToggleStyles};

  position: relative;
  display: flex;
  pointer-events: none;
  z-index: 1;

  > span {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    column-gap: 4px;
  }
`;
const SortSelect = styled.select `
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  border: none;
  background: none;
  color: ${brand.primary.blue};
  ${fonts.dmSans.regular};
  line-height: 1.5;
  cursor: pointer;
  z-index: 0;
  padding: 0 8px;
`;
const FilterToggle = styled.button `
  ${filterToggleStyles};

  @media ${from(Device.Tablet)} {
    display: none;
  }
`;
const FiltersInner = styled.div `
  position: sticky;
  top: var(--headerHeight, 0px);
  padding: ${fluid(24, 40)} 0 ${fluid(24, 40)} ${fluid(24, 40)};

  @media ${until(Device.Tablet)} {
    ${transition('transform')};
    position: fixed;
    top: 0;
    right: 0;
    transform: translateX(100%);
    width: 100%;
    height: 100vh;
    height: 100dvh;
    padding: ${fluid(24, 40)};
    background-color: ${brand.grey.light};
    z-index: ${Layers.Navigation + 1};

    &[data-active='true'] {
      transform: translateX(0);
    }
  }
`;
const CloseFilters = styled.button `
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 0 16px auto;
  padding: 0;
  background: none;
  border: none;
  color: ${brand.primary.blue};

  svg {
    width: 32px;
    height: 32px;
  }

  @media ${from(Device.Tablet)} {
    display: none;
  }
`;
const FiltersTitle = styled.h3 `
  ${h3};
`;
const FiltersList = styled.div `
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;
const FilterButton = styled(Button) `
  ${paragraphSmall};

  --buttonHeight: 34px;

  padding: 8px 12px;
  margin: 0;

  &[data-active='true'] {
    color: var(--colorContrast);
    background-color: var(--colorPrimary);
  }
`;
const Top = styled.div `
  ${leftColumns};

  display: flex;
  gap: ${fluid(16, 32)};

  @media ${until(Device.Tablet)} {
    flex-direction: column;
  }
`;
const TitleWrapper = styled.div `
  display: flex;
  gap: 12px;

  @media ${until(Device.Tablet)} {
    justify-content: center;
  }
`;
const NoResultsText = styled.p `
  ${paragraphLarge};

  grid-column: 1 / -1;
`;
const SearchResultsStyles = {
    CloseFilters,
    Container,
    FilterButton,
    FilterToggle,
    FilteringWrapper,
    Filters,
    FiltersInner,
    FiltersList,
    FiltersTitle,
    NoResultsText,
    ResultsBlock,
    ResultsCount,
    ResultsHeading,
    ResultsSort,
    ResultsTitle,
    SortSelect,
    SortLabel,
    Top,
    TitleWrapper,
};
export default SearchResultsStyles;
